import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../../../Legacy';
import { ApiClient } from '../../../api/ApiClient';
import { resolveImage } from '../../../api/ApiEndpoints';
import { useResolve } from '../../Container';
import { Popup, PopupProps } from '../../popups/Popup';
import { Colors, ImageFromProvider, ImageFromUrl, ImageResult, ImageUrlResolver, Prompt } from './../../../ui';
import { PixabayProvider } from './Pixabay';

const viaApiResolver = (api: ApiClient): ImageUrlResolver => async (url: string) => {
    const [result, ok] = await api.execs(resolveImage(url));
    return ok ? result.url : null;
};

type OptionResult = ImageResult & { policy: 'link' | 'host' };
type OptionProps = { callback: (data: OptionResult | null, confirm: boolean) => void };

const ImageFromComputer = styled.div`
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0.5rem;
    background-color: #f4f5f7;
    color: #abadb7;
    font-size: 1.4rem;
`;

const ImageFromComputerOption: LFC<OptionProps> = () => {

    return <ImageFromComputer>
        <div style={{ textAlign: 'center' }}>
            Drag and drop an image here to upload it to the board.  <br />
            You can also drop it onto the board directly!
        </div>
    </ImageFromComputer>;
};

const ImageFromUrlOption: LFC<OptionProps> = ({ callback }) => {

    const api = useResolve(ApiClient);
    const [value, setValue] = useState(null as ImageResult | null);
    const result = (ir: ImageResult) => ({ ...ir, policy: 'link' }) as OptionResult;

    return <ImageFromUrl
        resolver={viaApiResolver(api)}
        onSelect={v => {
            setValue(v);
            callback(result(v), false);
        }}
        onConfirm={() => callback(result(value), true)} />;
};

const ImageFromPixabayOption: LFC<OptionProps> = ({ callback }) => {

    const [value, setValue] = useState(null as ImageResult | null);
    const result = (ir: ImageResult) => ({ ...ir, policy: 'host' }) as OptionResult;

    return <ImageFromProvider
        provider={PixabayProvider}
        onSelect={v => {
            setValue(v);
            callback(result(v), false);
        }}
        onConfirm={() => callback(result(value), true)} />;
};

const menuOptions = [
    { id: 'fromComputer', label: 'From Computer', ui: ImageFromComputerOption },
    { id: 'fromPixabay', label: 'From Pixabay', ui: ImageFromPixabayOption },
    { id: 'fromURL', label: 'From URL', ui: ImageFromUrlOption },
];

type MenuOption = typeof menuOptions[0];

const Menu = styled.div`
    display: flex;
`;

const MenuButton = styled.div<{ selected: boolean }>`
    padding: .75rem 1rem;
    border-radius: .25rem;
    margin-right: .5rem;
    background: #f4f5f7;
    cursor: pointer;

    &:hover {
        background: ${Colors.primaryHover};
    }
    &:active {
        background: ${Colors.primary};
        color: white;
    }
    ${p => !p.selected ? '' : css`
        background: ${Colors.primary} !important;
        color: white;
    `}
`;

const Mount = styled.div`
    display: flex;
    flex: 1 1 auto;
    padding-top: 1rem;
`;

export const imagePopup = () => ({ ui: ImagePopup });

export class ImagePopup extends React.Component<PopupProps> {

    public state = {
        selectedOption: menuOptions[0],
        selectedResult: null as OptionResult | null,
    };

    public render() {
        const { selectedOption } = this.state;
        return <Prompt enterRequiresCtrl onAction={this.onPromptAction}>
            <Popup w={64} h={52}>
                <Popup.Close onAction={() => this.onPromptAction(false)} />
                <Popup.Title>Insert Image</Popup.Title>
                <Popup.Content>
                    <Menu>
                        {menuOptions.map((x, i) =>
                            <MenuButton key={i} selected={x === selectedOption} onClick={this.onMenuSelect(x)}>
                                {x.label}
                            </MenuButton>
                        )}
                    </Menu>
                    <Mount>{React.createElement(selectedOption.ui, { callback: this.onOptionReply })}</Mount>
                    {/* </Card> */}
                </Popup.Content>
            </Popup>
        </Prompt>;
    }

    private onMenuSelect = (opt: MenuOption) => () => {
        this.setState({ selectedOption: opt });
    };

    private onOptionReply = (result: OptionResult, confirm: boolean) => {
        this.setState({ selectedResult: result }, () => {
            if (confirm) {
                this.props.action(this.state.selectedResult);
            }
        });
    };

    private onPromptAction = (confirm: boolean) => {
        if (confirm && this.state.selectedResult) {
            this.props.action(this.state.selectedResult);
        }
        else {
            this.props.dismiss();
        }
    };
}
